import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';

import { SnackBarUtil } from '../qr-copy-message/snack-bar-util.service';
import { MockupDataModel } from '../qr-copy-message/mockup-data.model';
import { FeeService } from '../service/fee.service';
import { FeeStateService } from '../service/fee-state.service';
import { MemberPaymentRecord, MemberProfileResponse, MemberSubgroupInfo } from '../interface/fee.interface';
import { formatApiDateString } from '../utils/date.utils';

import {
  FeeEditPersonalInfoDialogComponent,
  EditPersonalInfoDialogData
} from '../fee-edit-personal-info-dialog/fee-edit-personal-info-dialog.component';
import {
  FeeEditEmergencyContactDialogComponent,
  EditEmergencyContactDialogData
} from '../fee-edit-emergency-contact-dialog/fee-edit-emergency-contact-dialog.component';
import {
  FeeRegisterSubgroupDialogComponent,
  RegisterSubgroupDialogData
} from '../fee-register-subgroup-dialog/fee-register-subgroup-dialog.component';
import {
  FeeRemoveSubgroupConfirmDialogComponent,
  RemoveSubgroupConfirmDialogData
} from '../fee-remove-subgroup-confirm-dialog/fee-remove-subgroup-confirm-dialog.component';
import {
  FeeEndBillingUnpaidDialogComponent,
  EndBillingUnpaidDialogData
} from '../fee-end-billing-unpaid-dialog/fee-end-billing-unpaid-dialog.component';
import {
  FeeApplyDiscountDialogComponent,
  ApplyDiscountDialogData
} from '../fee-apply-discount-dialog/fee-apply-discount-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-fee-member-profile',
  templateUrl: './fee-member-profile.component.html',
  styleUrls: ['./fee-member-profile.component.scss']
})
export class FeeMemberProfileComponent implements OnInit, OnDestroy {

  private readonly destroy$ = new Subject<void>();

  memberId!: number;
  industryId: number | null = null;
  profile: MemberProfileResponse | null = null;
  loading = true;
  sendingNotification = false;
  payingRecordId: number | null = null;
  endingRecordId: number | null = null;
  cancelingBinding = false;
  activeSubgroupId: number | null = null;
  private initialSubgroupId: number | null = null;
  showMobileInfo = false;
  showSubgroupInfoMobile = false;
  openMenuId: number | null = null;
  menuPosition: { top: number; left: number } | null = null;
  isMobile = typeof window !== 'undefined' && window.innerWidth <= 992;

  private readonly tabMenuWidth = 220;

  readonly itemsPerPage = 3;
  readonly minPageCountForNavigation = 4;
  currentPage = 0;
  readonly loadMorePageSize = 10;
  paymentsVisibleCount = this.loadMorePageSize;
  isLoading = false;

  readonly defaultAvatar = 'assets/svg/defaultUserIcon.svg';
  readonly currencySymbol = '֏';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly feeService: FeeService,
    private readonly feeStateService: FeeStateService,
    private readonly dialog: MatDialog,
    private readonly snackBar: SnackBarUtil,
    private readonly translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.memberId = Number(this.route.snapshot.paramMap.get('memberId'));
    const qIndustryId = this.route.snapshot.queryParamMap.get('industryId');
    this.industryId = qIndustryId ? Number(qIndustryId) : null;
    const qSubgroupId = this.route.snapshot.queryParamMap.get('subgroupId');
    this.initialSubgroupId = qSubgroupId ? Number(qSubgroupId) : null;
    this.loadProfile();
  }

  ngOnDestroy(): void {
    this.feeStateService.setFeeHeaderContext(null);
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:scroll')
  onScroll(): void {
    if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.isLoading) {
      this.loadMore();
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth <= 992;
  }

  private loadProfile(): void {
    this.loading = true;
    this.feeService.getMemberProfile(this.memberId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: profile => {
          this.profile = profile;
          this.loading = false;
          this.resetPaymentsVisibleCount();
          if (profile.subgroups.length > 0) {
            const preselected = this.initialSubgroupId != null
              ? profile.subgroups.find(s => s.id === this.initialSubgroupId)
              : undefined;
            this.activeSubgroupId = preselected ? preselected.id : profile.subgroups[0].id;
          }
          this.syncFeeHeaderContext();
        },
        error: () => {
          this.loading = false;
          this.showSnackbar(MockupDataModel.error(this.translateService.instant('fee.messages.failed_load_profile')), SnackBarUtil.CLASSES.error);
        }
      });
  }

  /** Unique fee groups derived from the member's enrolled subgroups. */
  get uniqueGroups(): { groupId: number; groupName: string; subgroupCount: number }[] {
    if (!this.profile) return [];
    const map = new Map<number, { groupId: number; groupName: string; subgroupCount: number }>();
    for (const sg of this.profile.subgroups) {
      if (!sg.groupId) continue;
      const entry = map.get(sg.groupId);
      if (entry) {
        entry.subgroupCount++;
      } else {
        map.set(sg.groupId, {
          groupId: sg.groupId,
          groupName: sg.groupName || '—',
          subgroupCount: 1,
        });
      }
    }
    return Array.from(map.values());
  }

  get activeSubgroup(): MemberSubgroupInfo | null {
    if (!this.profile || this.activeSubgroupId === null) return null;
    return this.profile.subgroups.find(s => s.id === this.activeSubgroupId) ?? null;
  }

  get filteredPayments(): MemberPaymentRecord[] {
    if (!this.profile) return [];
    if (this.activeSubgroupId === null) return this.profile.paymentHistory;
    return this.profile.paymentHistory.filter(p => p.subgroupId === this.activeSubgroupId);
  }

  get displayedPayments(): MemberPaymentRecord[] {
    return this.filteredPayments.slice(0, this.paymentsVisibleCount);
  }

  get hasMorePayments(): boolean {
    return this.paymentsVisibleCount < this.filteredPayments.length;
  }

  get isMemberActive(): boolean {
    const subgroup = this.activeSubgroup;
    if (subgroup) {
      return subgroup.active !== false;
    }
    return this.profile?.active !== false;
  }

  get isSubgroupBillingEnded(): boolean {
    return this.activeSubgroup?.billingEnded === true;
  }

  get isNotificationBlockedByBusinessRules(): boolean {
    if (!this.isMemberActive) {
      return true;
    }
    if (this.isSubgroupBillingEnded) {
      return true;
    }

    const payments = this.filteredPayments;
    if (payments.length === 0) return true;

    const hasActionableUnpaid = payments.some(payment => {
      const status = (payment.status || '').toUpperCase();
      return status === 'PENDING';
    });

    return !hasActionableUnpaid;
  }

  get allPaymentsPaid(): boolean {
    const payments = this.filteredPayments;
    return payments.length > 0
      && payments.every(p => (p.status || '').toUpperCase() === 'PAID');
  }

  get endTargetRecord(): MemberPaymentRecord | null {
    const payments = this.filteredPayments;
    if (payments.length === 0) {
      return null;
    }
    return payments[payments.length - 1];
  }

  showEndControl(record: MemberPaymentRecord): boolean {
    if (!this.isMemberActive || this.isScanMeUser) {
      return false;
    }
    const target = this.endTargetRecord;
    return target != null && target.id === record.id;
  }

  isEndButtonDisabled(record: MemberPaymentRecord): boolean {
    return this.endingRecordId === record.id || this.isSubgroupBillingEnded;
  }

  endButtonLabel(record: MemberPaymentRecord): string {
    if (this.isSubgroupBillingEnded) {
      return this.translateService.instant('fee.ended');
    }
    return this.translateService.instant('fee.end');
  }

  hasOutstandingBalanceForEnd(): boolean {
    return !this.allPaymentsPaid;
  }

  selectSubgroup(sg: MemberSubgroupInfo): void {
    this.activeSubgroupId = sg.id;
    this.resetPaymentsVisibleCount();
    this.syncFeeHeaderContext();
  }

  loadMore(): void {
    if (this.hasMorePayments) {
      this.paymentsVisibleCount += this.loadMorePageSize;
    }
  }

  get visibleSubgroups(): MemberSubgroupInfo[] {
    const subs = this.profile?.subgroups ?? [];
    const startIndex = this.currentPage * this.itemsPerPage;
    return subs.slice(startIndex, startIndex + this.itemsPerPage);
  }

  get tabSubgroups(): MemberSubgroupInfo[] {
    if (this.isMobile) {
      return this.profile?.subgroups ?? [];
    }
    return this.visibleSubgroups;
  }

  get maxPage(): number {
    const total = this.profile?.subgroups.length ?? 0;
    return Math.max(Math.ceil(total / this.itemsPerPage) - 1, 0);
  }

  nextPage(): void {
    if (this.currentPage < this.maxPage) {
      this.currentPage++;
    }
  }

  prevPage(): void {
    this.currentPage = 0;
  }

  toggleTabMenu(id: number, event: Event): void {
    if (!this.isMemberActive) return;
    event.stopPropagation();
    if (this.openMenuId === id) {
      this.closeTabMenu();
      return;
    }
    this.openMenuId = id;
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const left = Math.max(
      8,
      Math.min(rect.right - this.tabMenuWidth, window.innerWidth - this.tabMenuWidth - 8)
    );
    this.menuPosition = { top: rect.bottom + 6, left };
  }

  closeTabMenu(): void {
    this.openMenuId = null;
    this.menuPosition = null;
  }

  onToggleChange(event: Event): void {
    if (!this.profile) return;
    const nextActive = (event.target as HTMLInputElement).checked;
    if (!nextActive) {
      this.closeTabMenu();
    }

    const subgroupId = this.activeSubgroupId;
    if (subgroupId == null) {
      const prevActive = this.profile.active;
      this.profile = {...this.profile, active: nextActive};
      this.feeService.setMemberActive(this.memberId, nextActive)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          error: () => {
            this.profile = {...this.profile!, active: prevActive};
            this.showSnackbar(MockupDataModel.error(this.translateService.instant('fee.messages.failed_update_status')), SnackBarUtil.CLASSES.error);
          }
        });
      return;
    }

    const prevActive = this.activeSubgroup?.active ?? true;
    this.setSubgroupActive(subgroupId, nextActive);
    this.feeService.setEnrollmentActive(this.memberId, subgroupId, nextActive)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: () => {
          this.setSubgroupActive(subgroupId, prevActive);
          this.showSnackbar(MockupDataModel.error(this.translateService.instant('fee.messages.failed_update_status')), SnackBarUtil.CLASSES.error);
        }
      });
  }

  private setSubgroupActive(subgroupId: number, active: boolean): void {
    if (!this.profile) return;
    this.profile = {
      ...this.profile,
      subgroups: this.profile.subgroups.map(s => s.id === subgroupId ? {...s, active} : s)
    };
  }

  openEditPersonalInfo(): void {
    if (!this.profile || !this.isMemberActive) return;
    const config = new MatDialogConfig<EditPersonalInfoDialogData>();
    config.data = {
      memberId: this.memberId,
      profile: this.profile,
      subgroup: this.activeSubgroup ?? undefined,
    };
    config.panelClass = 'fee-dialog-panel';
    this.dialog.open(FeeEditPersonalInfoDialogComponent, config)
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((updated: MemberProfileResponse | undefined) => {
        if (updated) {
          this.profile = updated;
          this.resetPaymentsVisibleCount();
        }
      });
  }

  openEditEmergencyContact(): void {
    if (!this.profile || !this.isMemberActive) return;
    const config = new MatDialogConfig<EditEmergencyContactDialogData>();
    config.data = { memberId: this.memberId, profile: this.profile };
    config.panelClass = 'fee-dialog-panel';
    this.dialog.open(FeeEditEmergencyContactDialogComponent, config)
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((updated: MemberProfileResponse | undefined) => {
        if (updated) {
          this.profile = updated;
          this.resetPaymentsVisibleCount();
        }
      });
  }

  openRegisterSubgroup(): void {
    if (!this.profile || !this.isMemberActive) return;
    const previousSubgroupIds = new Set(this.profile.subgroups.map(s => s.id));
    const config = new MatDialogConfig<RegisterSubgroupDialogData>();
    config.data = {
      memberId: this.memberId,
      industryId: this.industryId ?? 0,
      currentSubgroups: this.profile.subgroups,
    };
    config.panelClass = 'fee-dialog-panel';
    this.dialog.open(FeeRegisterSubgroupDialogComponent, config)
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((updated: MemberProfileResponse | undefined) => {
        if (!updated) return;

        this.profile = updated;
        this.resetPaymentsVisibleCount();

        const newlyRegistered = updated.subgroups.find(s => !previousSubgroupIds.has(s.id));
        if (newlyRegistered) {
          this.activeSubgroupId = newlyRegistered.id;
          this.syncFeeHeaderContext();
          this.openApplyDiscountDialog(newlyRegistered);
          return;
        }

        if (updated.subgroups.length > 0 && !updated.subgroups.find(s => s.id === this.activeSubgroupId)) {
          this.activeSubgroupId = updated.subgroups[0].id;
        }
        this.syncFeeHeaderContext();
      });
  }

  openApplyDiscountDialog(subgroup: MemberSubgroupInfo): void {
    const config = new MatDialogConfig<ApplyDiscountDialogData>();
    config.data = {
      memberId: this.memberId,
      subgroup,
    };
    config.panelClass = 'fee-apply-discount-dialog-panel';
    config.width = '520px';
    config.maxWidth = '95vw';
    this.dialog.open(FeeApplyDiscountDialogComponent, config)
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((updated: MemberProfileResponse | undefined) => {
        if (!updated) return;
        this.profile = updated;
        this.resetPaymentsVisibleCount();
        this.syncFeeHeaderContext();
      });
  }

  openRemoveFromSubgroup(sg: MemberSubgroupInfo): void {
    if (!this.isMemberActive) return;
    const config = new MatDialogConfig<RemoveSubgroupConfirmDialogData>();
    config.data = { memberId: this.memberId, subgroup: sg };
    config.panelClass = 'fee-dialog-panel';
    this.dialog.open(FeeRemoveSubgroupConfirmDialogComponent, config)
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((removed: boolean | undefined) => {
        if (removed && this.profile) {
          const subgroups = this.profile.subgroups.filter(s => s.id !== sg.id);
          this.profile = { ...this.profile, subgroups };
          this.resetPaymentsVisibleCount();
          if (subgroups.length === 0) {
            this.activeSubgroupId = null;
          } else if (this.activeSubgroupId === sg.id || !subgroups.some(s => s.id === this.activeSubgroupId)) {
            this.activeSubgroupId = subgroups[0].id;
          }
          this.syncFeeHeaderContext();
          this.showSnackbar(MockupDataModel.success(this.translateService.instant('fee.messages.removed_from_subgroup', { subgroup: sg.name })), SnackBarUtil.CLASSES.success);
        }
      });
  }

  goToSubgroup(sg: MemberSubgroupInfo): void {
    if (this.industryId && sg.groupId) {
      this.router.navigate(['/fees', this.industryId, 'groups', sg.groupId]);
    }
  }

  goToGroup(groupId: number): void {
    if (this.industryId) {
      this.router.navigate(['/fees', this.industryId, 'groups', groupId]);
    }
  }

  get isScanMeUser(): boolean {
    return !!this.profile?.userId;
  }

  get showPaymentModeCard(): boolean {
    return this.isScanMeUser && !!this.activeSubgroup;
  }

  get isBindingPaymentActive(): boolean {
    return !!this.activeSubgroup?.recurringPaymentEnabled;
  }

  get paymentModeValueKey(): string {
    return this.isBindingPaymentActive
      ? 'fee.payment_mode.automatic_binding_value'
      : 'fee.payment_mode.manual_value';
  }

  get memberIdentifier(): string {
    return this.profile?.userId || String(this.memberId);
  }

  cancelRecurringBinding(): void {
    const subgroupId = this.activeSubgroup?.id;
    if (!this.profile || !subgroupId || !this.isBindingPaymentActive || this.cancelingBinding) return;

    const confirmed = window.confirm(this.translateService.instant('fee.messages.confirm_cancel_binding_payment'));
    if (!confirmed) return;

    this.cancelingBinding = true;
    this.feeService.cancelRecurringBinding(this.memberId, subgroupId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: profile => {
          this.cancelingBinding = false;
          this.profile = profile;
          if (!profile.subgroups.find(s => s.id === this.activeSubgroupId)) {
            this.activeSubgroupId = profile.subgroups[0]?.id ?? null;
          }
          this.syncFeeHeaderContext();
          this.showSnackbar(
            MockupDataModel.success(this.translateService.instant('fee.messages.cancel_binding_payment_success')),
            SnackBarUtil.CLASSES.success
          );
        },
        error: () => {
          this.cancelingBinding = false;
        }
      });
  }

  endPeriod(record: MemberPaymentRecord): void {
    if (!this.isMemberActive || this.endingRecordId !== null || this.isSubgroupBillingEnded) {
      return;
    }
    if (this.hasOutstandingBalanceForEnd()) {
      this.openEndUnpaidDialog();
      return;
    }
    this.executeEndPeriod(record);
  }

  private openEndUnpaidDialog(): void {
    const config = new MatDialogConfig<EndBillingUnpaidDialogData>();
    config.data = {
      memberId: this.memberId,
      subgroupId: this.activeSubgroupId,
    };
    config.panelClass = 'fee-dialog-panel';
    this.dialog.open(FeeEndBillingUnpaidDialogComponent, config)
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result === 'notify') {
          this.sendNotification();
        }
      });
  }

  private executeEndPeriod(record: MemberPaymentRecord): void {
    this.endingRecordId = record.id;
    this.feeService.endBillingPeriod(this.memberId, record.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: profile => {
          this.endingRecordId = null;
          this.profile = profile;
          this.resetPaymentsVisibleCount();
          if (profile.subgroups.length > 0 && !profile.subgroups.find(s => s.id === this.activeSubgroupId)) {
            this.activeSubgroupId = profile.subgroups[0].id;
          }
          this.syncFeeHeaderContext();
        },
        error: (err: HttpErrorResponse) => {
          this.endingRecordId = null;
          if (this.isUnpaidBalanceBeforeEndError(err)) {
            this.openEndUnpaidDialog();
            return;
          }
          this.showSnackbar(
            MockupDataModel.error(this.translateService.instant('fee.messages.failed_end_period')),
            SnackBarUtil.CLASSES.error
          );
        }
      });
  }

  private isUnpaidBalanceBeforeEndError(err: HttpErrorResponse): boolean {
    const message = err?.error?.message;
    return err?.status === 400 && message === 'UNPAID_BALANCE_BEFORE_END';
  }

  payRecord(record: MemberPaymentRecord): void {
    if (!this.isMemberActive || this.payingRecordId !== null) return;
    this.payingRecordId = record.id;
    this.feeService.markRecordPaidByAdmin(this.memberId, record.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.payingRecordId = null;
          this.loadProfile();
        },
        error: () => {
          this.payingRecordId = null;
          this.showSnackbar(MockupDataModel.error(this.translateService.instant('fee.messages.failed_mark_paid')), SnackBarUtil.CLASSES.error);
        }
      });
  }

  sendNotification(): void {
    if (this.sendingNotification || this.isNotificationBlockedByBusinessRules) return;
    this.sendingNotification = true;
    this.feeService.sendMemberNotification(this.memberId, this.activeSubgroupId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.sendingNotification = false;
          this.showSnackbar(MockupDataModel.success(this.translateService.instant('fee.messages.payment_reminder_sent')), SnackBarUtil.CLASSES.success);
        },
        error: (err: HttpErrorResponse) => {
          this.sendingNotification = false;
          if (err?.status === 429) {
            this.showSnackbar(
              MockupDataModel.error(this.translateService.instant('fee.messages.notification_cooldown')),
              SnackBarUtil.CLASSES.error
            );
            return;
          }
          this.showSnackbar(MockupDataModel.error(this.translateService.instant('fee.messages.failed_send_notification')), SnackBarUtil.CLASSES.error);
        }
      });
  }

  private showSnackbar(data: MockupDataModel, panelClassNames: string[]): void {
    this.snackBar.openSnackBar({
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: panelClassNames,
      data,
    });
  }

  goBack(): void {
    if (this.industryId) {
      this.router.navigate(['/fees', this.industryId]);
    } else {
      this.router.navigate(['/fees']);
    }
  }

  formatDate(d: string | undefined): string {
    return formatApiDateString(d, '/');
  }

  formatDateRange(sg: MemberSubgroupInfo): string {
    if (!sg.startDate) return '—';
    const from = this.formatDate(sg.startDate);
    return sg.endDate ? `${from} - ${this.formatDate(sg.endDate)}` : from;
  }

  frequencyLabel(value: string | undefined): string {
    if (!value) return '';
    const key = `fee.frequency_types.${value.toUpperCase()}`;
    const translated = this.translateService.instant(key);
    return translated !== key ? translated : value;
  }

  discountLabel(sg: MemberSubgroupInfo): string {
    if (!sg.discountValue) return '—';
    return sg.discountType === 'PERCENT'
      ? `${sg.discountValue}%`
      : `${sg.discountValue} ${this.currencySymbol}`;
  }

  paymentDiscountLabel(p: MemberPaymentRecord): string {
    if (!p.discount) return '—';
    return p.discountType === 'PERCENT' ? `${p.discount}%` : `${p.discount} ֏`;
  }

  paymentStatusClass(status: string): string {
    return 'status-' + (status || '').toLowerCase();
  }

  paymentStatusLabel(status: string): string {
    const key = (status || '').toUpperCase();
    if (key === 'PAID') return this.translateService.instant('payment_status.paid');
    if (key === 'PENDING') return this.translateService.instant('payment_status.pending');
    if (key === 'OVERDUE') return this.translateService.instant('fee.status.overdue');
    if (key === 'ENDED') return this.translateService.instant('fee.status.ended');
    return status || '';
  }

  getAvatarUrl(url: string | undefined): string {
    return url || this.defaultAvatar;
  }

  private syncFeeHeaderContext(): void {
    const subgroup = this.activeSubgroup;
    this.feeStateService.setFeeHeaderContext(
      subgroup
        ? {
          groupName: subgroup.groupName || '',
          subgroupName: subgroup.name || '',
          groupId: subgroup.groupId,
          subgroupId: subgroup.id,
        }
        : null
    );
  }

  private resetPaymentsVisibleCount(): void {
    this.paymentsVisibleCount = this.loadMorePageSize;
  }

}
